import React, { ReactNode, useRef } from "react";
import { useTranslation } from "next-i18next";
import cn from "classnames";
import { motion, useInView } from "framer-motion";
import RichText from "../../Common/RichText";
import s from "../InteractiveGrid.module.scss";
import Button from "../../Button";
import { RichTextContent } from "../../../lib/storyblok/types/fieldtypes/richText";
import BlockBackground from "../../Common/BlockBackground/BlockBackground";
import { useIsExternalLink } from "../../../hooks/useIsExternalLink";

interface ItemTouchProps {
  index: number;
  style?: string;
  title: string;
  description: RichTextContent;
  link?: string;
  image?: ReactNode;
  inverted?: boolean;
}

const ItemTouch: React.FC<ItemTouchProps> = ({
  index,
  style = "tobii-violet",
  title,
  description,
  link,
  image,
  inverted,
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
  });

  const { isExternalLink } = useIsExternalLink(link);

  return (
    <motion.div
      ref={ref}
      className={cn(s.card, s.cardTouch, { [s.inverted]: inverted })}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{
        opacity: isInView ? 1 : 0,
        scale: isInView ? 1 : 0.8,
      }}
      transition={{
        duration: 0.3,
        delay: index * 0.05,
        ease: [0.2, 0, 0.1, 1],
      }}
    >
      <div className={cn(s.visualContainer, s.imageContainer)}>{image}</div>
      <BlockBackground style={style} opacity={75} />
      <div className={s.contentContainer}>
        <span className={s.title}>{title}</span>
        {description && (
          <div className={s.description}>
            <RichText
              content={description}
              alignment="alignLeft"
              {...(inverted && { inverted: true })}
            />
          </div>
        )}
        {link && link !== "/" && link.trim() !== "" && (
          <Button
            variant={inverted ? "inverted" : "default"}
            size={"medium"}
            href={link}
            title={title}
            icon={!isExternalLink ? "arrow-right" : undefined}
          >
            {t("learn-more")}
          </Button>
        )}
      </div>
    </motion.div>
  );
};

export default ItemTouch;
